import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAnnualReportPdfRequest,
    IAnnualReportPdfSection,
    IPrintOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import Debug from 'debug';
import { v4 } from 'uuid';
import { MarkerClasses } from '../../enums/marker-classes.enum';
import { IPrintingUtilData } from '../../interfaces/IPrintingUtilData';
import { IReportSection } from '../../interfaces/IReportSection.interface';

export const debug = Debug('aumico:aumico-lib-annualreport-fe:PrintingUtil');

const DISABLE_GRAYSCALE = true;

export const WRAPPER_START_KEY = '###client-wapper-start###';
export const WRAPPER_END_KEY = '###client-wapper-end###';

export const FONT_STYLE_PLACEHOLDERS = `
    ###FontSourceSansPro###
    ###FontSourceSansProBold###
    ###FontPoppins###
`;
export const GRAY_SCALE_STYLE_ON = `<style>
    /* for the cover page */
    body > * {
        filter:grayscale(100%);
    }
    .extract-aum-annualreport-print-balancesheet *,
    .extract-aum-annualreport-print-incomestatement *,
    .extract-aum-annualreport-print-cashflow *,
    .extract-aum-annualreport-print-notes *,
    .extract-aum-annualreport-print-aop * {
        filter:grayscale(100%);
    }
</style>
`;
export const GRAY_SCALE_STYLE_OFF = `<style>
</style>
`;
// const grayscaleStylePlaceholder = '###client-grayscale-style###';

const COMPANY_NAME_IN_HEADER_FONT_FAMILY_NAME = 'Source Sans Pro'; /* Black & White/black-80 */

const FONT_FACTOR = 0.24 / 0.16;
const COMPANY_NAME_IN_HEADER_FONT_SIZE = `${0.24 * FONT_FACTOR}cm`;

const SECTION_TITLE_NAME_IN_HEADER_FRONT_SIZE = `${0.44 * FONT_FACTOR}cm`;
const PAGE_NUMBER_IN_FOOTER = `${0.24 * (21 / 22) * FONT_FACTOR}cm`;

// 0.1 is the line height of the font immediately after
const HEADER_ABOVE_CONTENT_MARGIN = `${1.26 - 0.1}cm`;
const FOOTER_BELOW_CONTENT_MARGIN = `${0.37 - 0.16}cm`;

// the upper container already has a margin and the lower container margin -0.2
const MARGIN_BEFORE_TITLE_SECTION = `${0.69 - 0.2}cm`;

const PADDING_LEFT = `${0}cm`;

const COMPANY_NAME_IN_HEADER_COLOR = '#4E4952'; /* Black & White/black-80 */

const HEADER_SECTION_FONT_FAMILY_NAME = 'Poppins';
const ENCODING_META = '<meta charset="utf-8">';

export class PrintingUtil {
    private appId = v4();
    private printOptionsData: IPrintOptions | undefined;
    private isShowAuthoringCompany: boolean;
    private printPreviewPageClassNameSelector: string;
    private authoringCompanyName: string;
    private companyName: string;

    private companyLogoPdfBase64Getter: () => string | null;
    private reportSections: IReportSection[] = [];

    constructor(private data: IPrintingUtilData) {
        Object.assign(this, data);
    }

    public getPrintableContent(): IAnnualReportPdfRequest {
        let wrappingPlaceHolderStartEnd = [];
        const shadowDom = document.cloneNode(true) as Document;
        try {
            shadowDom.getElementsByTagName('body')[0].innerHTML = shadowDom.getElementsByClassName(
                this.printPreviewPageClassNameSelector,
            )[0].outerHTML;

            const style = `*{box-sizing: border-box;}[hidden]{display:none}`;
            shadowDom.getElementsByTagName('style')[0].innerHTML = style;
        } catch (error) {
            debug(error);
        }
        // let wrappingHtml: string = shadowDom.documentElement.outerHTML;
        const sections: IAnnualReportPdfSection[] = new Array<IAnnualReportPdfSection>();

        const shadowWrapper = shadowDom.cloneNode(true) as Document;
        const sectionsDom = shadowDom.cloneNode(true) as Document;

        // since the content is using the same wrapper it should be ok to put it here
        // note that a client placeholder will not be replaced in the supplied client placeholders
        try {
            const splittingToken = `@@@${v4()}@@@`;
            // we can fallback with this if we want
            // shadowDom.getElementsByTagName('style')[0].outerHTML += this.getGrayScaleStyle();
            // remove the grayscale class
            // filter: grayscale(100%) style messes up the elements with position  fixed;
            shadowWrapper.getElementsByTagName('style')[0].outerHTML += this.getGrayScaleStyle();
            shadowWrapper
                .getElementsByClassName(this.printPreviewPageClassNameSelector)[0]
                .classList.remove('grayscale');

            shadowWrapper.getElementsByClassName(this.printPreviewPageClassNameSelector)[0].innerHTML = splittingToken;
            shadowWrapper.head.innerHTML += FONT_STYLE_PLACEHOLDERS;
            wrappingPlaceHolderStartEnd = shadowWrapper.documentElement.outerHTML.split(splittingToken);
        } catch (error) {
            debug(error);
        }

        // notice that the function dose not mutate the dom so it can be reused
        // if we need
        const getDom = () => sectionsDom;
        for (const sectionDeclaration of this.reportSections) {
            const markerClassName = sectionDeclaration.markerClassName;
            const header = this.getSectionHeaderFromTitle(sectionDeclaration.title);
            const footer = this.getSectionFooter();
            const id = sectionDeclaration.id;
            const dom = getDom();
            const sectionItem = this.getSectionItem(dom, id, markerClassName, header, footer);
            if (sectionItem != null) {
                sections.push(sectionItem);
            }
        }

        // cover page
        let coverPage: IAnnualReportPdfSection | null = null;
        if (this.printOptionsData != null && this.printOptionsData.displayOptions.showCoverSheet) {
            const markerClassName = MarkerClasses.COVER_PAGE;

            const grayScaleStyleLiteral = this.getGrayScaleStyleLiteral();
            const appId = this.appId;
            const authoringCompanyName = this.authoringCompanyName;
            const isShowAuthoringCompany = this.isShowAuthoringCompany;
            const header = this.getHeaderForCover(
                grayScaleStyleLiteral,
                appId,
                isShowAuthoringCompany,
                authoringCompanyName,
            );
            const footer = this.getFooterForCover();
            const id = 'fixed-cover';
            const dom = getDom();
            coverPage = this.getSectionItem(dom, id, markerClassName, header, footer);
        }

        let tableOfContents: IAnnualReportPdfSection | null = null;
        if (this.printOptionsData != null && this.printOptionsData.displayOptions.showTableOfContents) {
            const grayScaleStyleLiteral = this.getGrayScaleStyleLiteral();
            const appId = this.appId;
            const companyName = this.companyName;
            const header = this.getHeaderForToc(grayScaleStyleLiteral, appId, companyName);
            const footer = this.getFooterForToc();
            const dom = getDom();
            tableOfContents = this.getTableOfContentReorderedPrintableSection(dom, header, footer);
        }
        let startPage = 1;
        const isPrintOptionDataExist = this.printOptionsData != null;

        let arePrintOptionsExist = false;
        if (isPrintOptionDataExist) {
            arePrintOptionsExist =
                this.printOptionsData.displayOptions != null &&
                this.printOptionsData.displayOptions.showCustomPageNumbers != null &&
                this.printOptionsData.displayOptions.startAtPage != null;
        }
        if (arePrintOptionsExist) {
            const savedStartPageNumber = this.printOptionsData.displayOptions.startAtPage;

            if (savedStartPageNumber != null && NumberUtils.isFinite(savedStartPageNumber)) {
                startPage = NumberUtils.getIntegerValue(savedStartPageNumber);
            } else {
                startPage = 1;
            }
        }

        // Sort sections according to reportOrder
        let sortedSections = [];
        if (this.printOptionsData.reportOrder && this.printOptionsData.reportOrder.length > 0) {
            const reportsOrder = this.printOptionsData.reportOrder;
            reportsOrder.forEach((val) => {
                const section = sections.find((reportSection) => reportSection.id === val);
                if (section) {
                    sortedSections.push(section);
                }
            });
        } else {
            sortedSections = [...sections];
        }

        const content: IAnnualReportPdfRequest = {
            // wrappingHtml,
            sections: sortedSections,
            coverPage,
            tableOfContents,
            clientProvidedPlaceHoldersContent: {},
            settings: {
                currentPageOffset: startPage - 1,
                totalPageOffset: startPage - 1,
            },
            companyLogoPdfBase64: null,
        };
        // once we put the placeholder mechanism in place, we can extend it as we wish for content, headers footers etc.
        content.clientProvidedPlaceHoldersContent[WRAPPER_START_KEY] = wrappingPlaceHolderStartEnd[0];
        content.clientProvidedPlaceHoldersContent[WRAPPER_END_KEY] = wrappingPlaceHolderStartEnd[1];
        content.companyLogoPdfBase64 =
            this.companyLogoPdfBase64Getter == null ? null : this.companyLogoPdfBase64Getter();
        return content;
    }

    private getSectionItem(
        dom: Document,
        id: string | 'fixed-cover',
        markerClassName: string,
        header: string,
        footer: string,
    ): IAnnualReportPdfSection | null {
        try {
            const sectionHTMLCollection = dom.getElementsByClassName(markerClassName);
            if (sectionHTMLCollection.length !== 1) {
                throw new Error('Must have only 1 section with the same marker class name');
            }
            const html = sectionHTMLCollection[0].outerHTML;

            return {
                content: `${WRAPPER_START_KEY}
                    ${html}
                    ${WRAPPER_END_KEY}`,
                header,
                footer,
                id,
            };
        } catch (error) {
            debug(error);
            return null;
        }
    }

    private getTableOfContentReorderedPrintableSection(
        dom: Document,
        header: string,
        footer: string,
    ): IAnnualReportPdfSection {
        const id = 'fixed-cover';
        const tocMarkerClassName = MarkerClasses.TOC;

        const tocDomElementHTMLCollection = dom.getElementsByClassName(tocMarkerClassName);
        if (tocDomElementHTMLCollection.length !== 1) {
            throw new Error('Must have only 1 section with the same marker class name');
        }
        const tocDomElement = tocDomElementHTMLCollection[0];

        const sectionIdToNodeMap = new Map<string, Node>();

        this.reportSections.forEach((section) => {
            const sectionHTMLCollection = tocDomElement.getElementsByClassName(section.id);

            if (sectionHTMLCollection == null || sectionHTMLCollection.length === 0) {
                return;
            }

            if (sectionHTMLCollection.length > 1) {
                throw new Error('Must have only 1 section with the same marker class name' + section.markerClassName);
            }

            const sectionItemNode = sectionHTMLCollection[0].cloneNode(true);
            sectionIdToNodeMap.set(section.id, sectionItemNode);
        });

        this.reportSections.forEach((section, index: number) => {
            const element = tocDomElement.getElementsByClassName('toc-content-items')[index];
            if (element == null) {
                return;
            }

            if (element.childNodes[0] == null) {
                throw new Error('There are no child nodes to be replaced');
            }
            if (element.childNodes.length !== 1) {
                throw new Error('There Must be only 1 child node for each toc-content-item');
            }

            const sectionNode = sectionIdToNodeMap.get(section.id);
            if (sectionNode == null) {
                return;
            }

            element.replaceChild(sectionNode, element.childNodes[0]);
        });

        const tocHtml = tocDomElement.outerHTML;

        return {
            content: `${WRAPPER_START_KEY}
                    ${tocHtml}
                    ${WRAPPER_END_KEY}`,
            header,
            footer,
            id,
        };
    }

    private getGrayScaleStyle(): string {
        if (this.isShowGrayscale) {
            return GRAY_SCALE_STYLE_ON;
        } else {
            return GRAY_SCALE_STYLE_OFF;
        }
    }

    private getGrayScaleStyleLiteral(): string {
        if (this.isShowGrayscale) {
            return 'filter:grayscale(100%);';
        } else {
            return '';
        }
    }

    get isShowGrayscale(): boolean {
        if (DISABLE_GRAYSCALE) {
            return false;
        }
        if (this.printOptionsData && this.printOptionsData.displayOptions) {
            return this.printOptionsData.displayOptions.printInGrayscale;
        }
        return false;
    }

    // used for debugging
    private debugPageElement(shadowDomNotes: Document) {
        const newWindow4 = window.open();
        newWindow4.document.write(shadowDomNotes.documentElement.outerHTML);
    }

    private getSectionHeaderFromTitle(title: string): string {
        const grayScaleStyleLiteral = this.getGrayScaleStyleLiteral();
        const appId = this.appId;
        const companyName = this.companyName;
        return this.getHeaderForSection(grayScaleStyleLiteral, appId, companyName, title);
    }

    private getSectionFooter(): string {
        const grayScaleStyleLiteral = this.getGrayScaleStyleLiteral();
        const appId = this.appId;
        return this.getFooterForSection(grayScaleStyleLiteral, appId);
    }

    private getFooterForSection(grayScaleStyleLiteral: string, appId: string): string {
        return `
            <style>
            .c_${appId}-page-number {
                font-family: ${COMPANY_NAME_IN_HEADER_FONT_FAMILY_NAME};
                font-style: normal;
                line-height: 160%;
                font-size: calc(1* ${PAGE_NUMBER_IN_FOOTER});
                color: ${COMPANY_NAME_IN_HEADER_COLOR};
                padding-left: ${PADDING_LEFT};
                padding-right: calc( 0.25cm) ;
                flex-shrink: 1;
                }
            </style>
            <div style="height: 1.5cm;width: 100%;display: flex;flex-direction: column-reverse; ${grayScaleStyleLiteral}">
                <div class="separator-1" style="min-height: ${FOOTER_BELOW_CONTENT_MARGIN}; max-height:${FOOTER_BELOW_CONTENT_MARGIN}" ></div>
                <div style="width: 100%;display: flex;flex-direction: row-reverse; ${grayScaleStyleLiteral}">
                    <span class="c_${appId}-page-number"> ###pageNumber### / ###pageCount###</span>
                </div>
            </div>
        `;
    }

    private getHeaderForSection(
        grayScaleStyleLiteral: string,
        appId: string,
        companyName: string,
        title: string,
    ): string {
        return `
                ${ENCODING_META}
                ${FONT_STYLE_PLACEHOLDERS}
                <style>
                .c_${appId}-header-company-name {
                    font-family: ${COMPANY_NAME_IN_HEADER_FONT_FAMILY_NAME};
                    font-style: normal;
                    line-height: 160%;
                    font-size: calc(1* ${COMPANY_NAME_IN_HEADER_FONT_SIZE});
                    color: ${this.printOptionsData.styleOptions.textColor};
                    padding-left: ${PADDING_LEFT};
                    flex-shrink: 1;
                }
                .c_${appId}-header {
                    font-family: ${HEADER_SECTION_FONT_FAMILY_NAME};
                    font-style: normal;
                    font-weight:normal;
                    line-height: 145%;
                    font-size: calc(1* ${SECTION_TITLE_NAME_IN_HEADER_FRONT_SIZE});
                    color: ${this.printOptionsData.styleOptions.textColor};
                    padding-left: ${PADDING_LEFT};
                    flex-shrink: 1;
                }

                </style>
               <div  style="height: 4cm;width: 100%;display: flex;flex-direction: column; ${grayScaleStyleLiteral}">
                    <div class="separator-1" style="min-height: ${HEADER_ABOVE_CONTENT_MARGIN}; max-height:${HEADER_ABOVE_CONTENT_MARGIN}" ></div>
                    <span class="c_${appId}-header-company-name">${companyName}</span>
                    <div style=" min-height: ${MARGIN_BEFORE_TITLE_SECTION}; min-height: ${MARGIN_BEFORE_TITLE_SECTION}; width: 100%"></div>
                    <h1 class="c_${appId}-header">${title}</h1>
               </div>
           `;
    }

    private getHeaderForCover(
        grayScaleStyleLiteral: string,
        appId: string,
        isShowAuthoringCompany: boolean,
        authoringCompanyName: string,
    ): string {
        return `
        ${ENCODING_META}
        ${FONT_STYLE_PLACEHOLDERS}
                <style>
                    .c_${appId}-header-company-name {
                        font-family: ${COMPANY_NAME_IN_HEADER_FONT_FAMILY_NAME};
                        font-style: normal;
                        line-height: 160%;
                        font-size: calc(1* ${COMPANY_NAME_IN_HEADER_FONT_SIZE});
                        color: ${this.printOptionsData.styleOptions.textColor};
                        padding-left: ${PADDING_LEFT};
                        flex-shrink: 1;
                    }
                </style>
                <div  style="height: 4cm;width: 100%;display: flex;flex-direction: column; ${grayScaleStyleLiteral}">
                    <div class="separator-1" style=" min-height: ${HEADER_ABOVE_CONTENT_MARGIN}; max-height:${HEADER_ABOVE_CONTENT_MARGIN}" ></div>
                    <span class="c_${appId}-header-company-name">${
            isShowAuthoringCompany ? authoringCompanyName || '' : ''
        }
                    </span>
                </div>
                `;
    }

    private getFooterForToc(): string {
        return `
                    <div>
                        <span></span>
                    </div>
                `;
    }

    private getFooterForCover(): string {
        return `
                <span></span>
            `;
    }

    private getHeaderForToc(grayScaleStyleLiteral: string, appId: string, companyName: string): string {
        return `
        ${ENCODING_META}
        ${FONT_STYLE_PLACEHOLDERS}
        <style>
            .c_${appId}-header-company-name {
                font-family: ${COMPANY_NAME_IN_HEADER_FONT_FAMILY_NAME};
                font-style: normal;
                line-height: 160%;
                font-size: calc(1* ${COMPANY_NAME_IN_HEADER_FONT_SIZE});
                color: ${this.printOptionsData.styleOptions.textColor};
                padding-left: ${PADDING_LEFT};
                flex-shrink: 1;
            }
        </style>
                <div  style="height: 4cm;width: 100%;display: flex;flex-direction: column; ${grayScaleStyleLiteral}">
                    <div class="separator-1" style=" min-height: ${HEADER_ABOVE_CONTENT_MARGIN}; max-height:${HEADER_ABOVE_CONTENT_MARGIN}" ></div>

                    <span class="c_${appId}-header-company-name">${companyName || ''}</span>
                </div>
                `;
    }
}
